import React, { useState, useEffect } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import { MdSpeed, MdBackup, MdNotificationsActive, MdAttachMoney, MdAnalytics ,MdAssessment} from "react-icons/md";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useMonthlyReport } from "../../hooks/aiHooks/useMonthlyReport";
import TopLayerBoxes from "../../components/monthlyReportComponents/topLayerboxes";
import AlertsCharts from "../../components/monthlyReportComponents/alertsCharts";
import AIAlertInsights from "../../components/monthlyReportComponents/AIAlertInsights";
import PerformanceCharts from "../../components/monthlyReportComponents/PerformanceCharts";
import AIPerformanceInsights from "../../components/monthlyReportComponents/AIPerformanceInsights";
import MonthlyCostChart from "../../components/monthlyReportComponents/MonthlyCostChart";
import AICostInsights from "../../components/monthlyReportComponents/AICostInsights";
import AIInfrastructureSummary from "../../components/monthlyReportComponents/AIInfrastructureSummary";
import BackupStatsCards from "../../components/monthlyReportComponents/BackupStatsCards";
import AIBackupInsights from "../../components/monthlyReportComponents/AIBackupInsights";

const SectionHeader = ({ icon: Icon, title }) => {
  return (
    <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 mt-4 sm:mt-6">
      <Icon className="text-primary-cyan text-2xl sm:text-3xl flex-shrink-0" />
      <h2 className="text-primary-dark text-sm sm:text-base md:text-lg font-bold uppercase tracking-wide">
        {title}
      </h2>
      <div className="flex-1 h-px bg-gradient-to-r from-primary-cyan/30 to-transparent"></div>
    </div>
  );
};

const MonthlyReportPage = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { getMonthlyReportByMonthAndYear } = useMonthlyReport();

  useEffect(() => {
    loadLatestReport();
  }, []);

  const loadLatestReport = async () => {
    setLoading(true);
    const currentDate = new Date();
    let latestReport = null;
    let latestDate = null;

    for (let i = 0; i < 12; i++) {
      const checkDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1,
      );
      if (checkDate.getFullYear() < 2024) break;

      const result = await getMonthlyReportByMonthAndYear(
        checkDate.getFullYear(),
        checkDate.getMonth() + 1,
      );
      if (result.success && result.data) {
        latestReport = result.data;
        latestDate = checkDate;
        break;
      }
    }

    if (latestReport && latestDate) {
      setReportData(latestReport);
      setSelectedDate(latestDate);
    }
    setLoading(false);
  };

  const handleDateChange = async (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    setLoading(true);
    const result = await getMonthlyReportByMonthAndYear(year, month);
    if (result.success && result.data) {
      setReportData(result.data);
      setSelectedDate(date);
    } else {
      alert(
        `No report available for ${date.toLocaleString("default", { month: "long", year: "numeric" })}`,
      );
    }
    setLoading(false);
  };

  const formatMonthDisplay = () => {
    if (!selectedDate) return "Select month";
    return selectedDate.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8 overflow-x-hidden max-w-full">
      <div className="flex justify-start mb-4 sm:mb-6">
        <div className="relative w-full sm:w-auto">
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            maxDate={new Date()}
            minDate={new Date(2024, 0, 1)}
            customInput={
              <button className="flex items-center justify-center gap-2 bg-primary-cyan hover:bg-primary-cyan/90 text-white px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 rounded-lg transition-all duration-200 font-medium text-xs sm:text-sm md:text-base w-full sm:w-auto">
                <FaCalendarAlt size={14} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                <span className="truncate max-w-[150px] sm:max-w-none">{formatMonthDisplay()}</span>
              </button>
            }
            popperPlacement="bottom-end"
            popperClassName="z-50"
            calendarClassName="bg-primary-dark border border-gray-700 rounded-lg shadow-xl"
            wrapperClassName="w-full sm:w-auto"
          />
        </div>
      </div>

      <div className="mb-6 sm:mb-8">
        <h1 className="text-primary-dark text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold break-words">
          Monthly Report - {formatMonthDisplay()}
        </h1>
        <p className="text-primary-dark text-sm sm:text-base md:text-lg mt-1 font-medium">
          Sindibad Infrastructure
        </p>
      </div>

      <TopLayerBoxes reportData={reportData} loading={loading} />

      <SectionHeader icon={MdSpeed} title="PERFORMANCE METRICS" />
      <PerformanceCharts reportData={reportData} loading={loading} />
      <AIPerformanceInsights reportData={reportData} loading={loading} />

      <SectionHeader icon={MdBackup} title="BACKUP STATUS" />
      <BackupStatsCards reportData={reportData} loading={loading} />
      <AIBackupInsights reportData={reportData} loading={loading} />

      <SectionHeader icon={MdNotificationsActive} title="ALERT ANALYSIS" />
      <AlertsCharts reportData={reportData} loading={loading} />
      <AIAlertInsights reportData={reportData} loading={loading} />

      <SectionHeader icon={MdAttachMoney} title="COST BREAKDOWN" />
      <MonthlyCostChart reportData={reportData} loading={loading} />
      <AICostInsights reportData={reportData} loading={loading} />

      <SectionHeader icon={MdAssessment} title="INFRASTRUCTURE SUMMARY" />
      <AIInfrastructureSummary reportData={reportData} loading={loading} />
    </div>
  );
};

export default MonthlyReportPage;